import React, {useCallback, useState} from 'react';
import {amountFormatter, isArrayEmpty, showError, showSuccess} from '@utils/helpers';
import Product from "@components/Checkout/Product";
import {HiArrowLeft} from "react-icons/hi";
import {GrRestaurant} from "react-icons/gr";
import {BiPhone} from "react-icons/bi";
import SmsCode from "@components/Checkout/SmsCode";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {useDispatch} from "react-redux";
import {hideSpinner, showSpinner} from "@redux/spinner/Action";
import TableOrderService from "@services/TableOrderService";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

const Checkout = ({
                    userLocation,
                    businessId,
                    businessName,
                    table,
                    selectedProducts,
                    setSelectedProducts,
                    total,
                    paymentMethods,
                    setActiveForm,
                  }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(paymentMethods[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [openPhoneNumberValidation, setOpenPhoneNumberValidation] = useState(false);

  const {executeRecaptcha} = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }
    return await executeRecaptcha()
  }, [executeRecaptcha]);
  const orderSubmission = async () => {
    const token = await handleReCaptchaVerify()
    dispatch(showSpinner('Please wait...'));
    const payload = {
      description: orderDetails,
      clientPhone: phoneNumber ? phoneNumber : undefined,
      paymentMethod: selectedPaymentMethod === 'CASH' ? 0 : 3,
      reCaptchaToken: token,
      coordinates: {
        lat: userLocation?.latitude,
        lng: userLocation?.longitude
      },
      orderProducts: selectedProducts.map(({id, qty}) => {
        return {productId: id, qty};
      }),
    }
    TableOrderService.createOrder(businessId, table?.id, payload)
      .then((response) => {
        dispatch(hideSpinner());
        setSelectedProducts([]);
        setSelectedPaymentMethod(paymentMethods[0]);
        setPhoneNumber('');
        setOrderDetails('');
        showSuccess(t('orderCreatedSuccessfully'));
        if (response.data.pokGatewayUrl) {
          window.open(`${response.data.pokGatewayUrl}`, "_self")
        } else {
          navigate(`/orders/details/${response?.data?.id}?phoneNumber=${phoneNumber}`);
        }
      })
      .catch((err) => {
        dispatch(hideSpinner());
        showError(err.response.data.message);
      })
      .finally(() => {
        handleReCaptchaVerify();
      });
  }

  const onSubmit = async () => {
    const token = await handleReCaptchaVerify();
    if (phoneNumber) {
      TableOrderService.phoneNumber(phoneNumber, token)
        .then(async (response) => {
          if (response.data === "VERIFICATION_SENT") {
            setOpenPhoneNumberValidation(!openPhoneNumberValidation)
          } else {
            orderSubmission();
          }
        })
        .catch((err) => {
          showError(err.response.data.message);
        })
    } else {
      orderSubmission();
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className='transition duration-300 z-20 w-full rounded-b-4xl drop-shadow-lg h-14'
        style={{
          position: 'sticky',
          top: 0,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundColor: 'white',
          zIndex: 1,
        }}>
        <div style={{zIndex: 9999}}
          className="absolute bottom-3 px-5 text-opacity-90 font-semibold whitespace-nowrap inline-flex items-center align-middle hover:cursor-pointer"
          onClick={() => setActiveForm('menu')}>
          <HiArrowLeft className="text-blue-800 cursor-pointer font-bold"/><span
          className='ml-2'>{t('checkout')}</span>
        </div>
      </div>
      <div className="flex flex-col h-screen justify-between mx-5">
        <div className="w-full bg-white rounded-xl shadow-even justify-between items-center">
          <div className="relative mt-5 mb-3">
            <input
              placeholder={t('phoneNumber')}
              value={phoneNumber}
              type='text'
              className='appearance-none relative block w-full mt-2 pl-10 h-11 border border-gray-300 placeholder-gray-500 rounded-lg text-gray-900 focus:outline-none focus:ring-gray-500 focus:border-blue-800 text-sm'
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <div
              className="absolute top-0 h-full left-3 flex justify-center items-center cursor-pointer">
              <BiPhone/>
            </div>
          </div>
          {!isArrayEmpty(selectedProducts) && <div
            className="py-2 text-opacity-90 font-semibold whitespace-nowrap inline-flex items-center align-middle">
            Order in {businessName}
          </div>}
          {selectedProducts?.map((product) => {
            const selectedQty = selectedProducts.find((p) => p.id === product.id)?.qty || 0;
            return (
              <Product key={product.id} product={product} selectedProducts={selectedProducts}
                       setSelectedProducts={setSelectedProducts}
                       selectedQty={selectedQty}/>
            );
          })}
          <div className="relative mt-5 mb-3">
            <input placeholder={t('orderDetails')}
                   value={orderDetails}
                   type='text'
                   className='appearance-none relative block w-full mt-2 pl-10 h-11 border border-gray-300 placeholder-gray-500 rounded-lg text-gray-900 focus:outline-none focus:ring-gray-500 focus:border-blue-800 text-sm'
                   onChange={(e) => setOrderDetails(e.target.value)}
            />
            <div
              className="absolute top-0 h-full left-3 flex justify-center items-center cursor-pointer">
              <GrRestaurant/>
            </div>
          </div>
          <div
            className="flex flex-col mb-2 p-3 transition duration-300 rounded-lg shadow-md">
            <div
              className="pb-2 text-opacity-90 font-semibold whitespace-nowrap inline-flex items-center align-middle">
              {t('paymentMethod')}
            </div>
            <div className="flex gap-10 mt-2">
              {paymentMethods.map((paymentMethod) => {
                return (
                  <div key={paymentMethod} className="inline-flex items-center"
                       onClick={() => setSelectedPaymentMethod(paymentMethod)}>
                    <label
                      className="relative flex cursor-pointer items-center rounded-full"
                      htmlFor="ripple-on"
                      data-ripple-dark="true"
                    >
                      <input
                        id="ripple-on"
                        name="ripple"
                        type="radio"
                        className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-full border border-blue-gray-200 text-blue-800 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-8 before:w-8 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-blue-800 checked:before:bg-blue-800 hover:before:opacity-10"
                        checked={selectedPaymentMethod === paymentMethod}
                      />
                      <div
                        className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-blue-800 opacity-0 transition-opacity peer-checked:opacity-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3.5 w-3.5"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          <circle data-name="ellipse" cx="8" cy="8" r="8"></circle>
                        </svg>
                      </div>
                    </label>
                    <label
                      className="ml-3 cursor-pointer select-none font-inter font-light tracking-wide leading-5 text-sm text-gray-700 flex-wrap"
                      htmlFor="ripple-on"
                    >
                      {paymentMethod === 'POK' ? 'Mastercard / Visa / POK' : t('cash')}
                    </label>
                  </div>
                )
              })
              }
            </div>
          </div>
        </div>
        {selectedProducts.length > 0 &&
          <div className="sticky w-full bottom-0 flex items-center justify-end py-3">
            <button onClick={() => onSubmit()}
                    className="py-4 px-2 text-base group w-full relative justify-center font-medium rounded-3xl text-white bg-gradient-to-r from-cyan-600 from-10% via-sky-800 via-30% to-blue-800 to-90% hover:bg-gradient-to-r hover:from-cyan-600 hover:from-10% hover:via-sky-900 hover:via-30% hover:to-blue-900 to-90 focus:outline-none">
              <div className='flex justify-between'>
                <div className="flex items-center">
                  <div
                    className="h-6 w-6 backdrop-blur bg-white/50 rounded-full mx-3 active:opacity-70 active:text-opacity-70 cursor-pointer transition duration-300">
                    {selectedProducts.length}
                  </div>
                  <div className='ml-3'>{t('placeYourOrder')}</div>
                </div>
                <div className="font-[600] mr-2">{amountFormatter(total, 'ALL')}</div>
              </div>
            </button>
          </div>}
      </div>
      {openPhoneNumberValidation &&
        <SmsCode phoneNumber={phoneNumber}
                 onSuccess={orderSubmission}
                 isOpen={openPhoneNumberValidation}
                 modalState={setOpenPhoneNumberValidation}
                 generateNewToken={handleReCaptchaVerify}
        />}
    </div>
  );
};

export default Checkout;
